import { hasPermission } from "@edm/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Bell, ChevronDown, LogOut, Menu, Search, Settings, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BrandMark } from "@/components/brand-mark";
import { GlobalSearch } from "@/components/global-search";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { APP_NAME, APP_VERSION, SOP_VERSION } from "@/config/app";
import { NAV_GROUPS, PAGE_TITLES } from "@/config/nav";
import { apiFetch } from "@/lib/api";
import { errorMessage, queryClient, useLogout, useMe } from "@/lib/auth";
import { cn, formatDateTime, initials } from "@/lib/utils";

function SidebarContent({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) {
  const { data: me } = useMe();
  const permissions = me?.permissions ?? [];
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.permission || hasPermission(permissions, item.permission),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-3 px-5">
        <BrandMark />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{APP_NAME}</p>
          <p className="text-[11px] text-sidebar-muted">EDM · Cloudflare</p>
        </div>
      </div>
      <Separator className="bg-white/10" />
      <nav className="app-scrollbar flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-2 pb-2 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to as never}
                    onClick={onNavigate}
                    className={cn(
                      "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-sidebar-foreground transition-colors hover:bg-white/10 hover:text-white",
                      active && "bg-white/10 text-white shadow-sm",
                    )}
                  >
                    <item.icon
                      className={cn("h-4 w-4", active ? "text-blue-300" : "text-sidebar-muted")}
                    />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {active ? <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> : null}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="space-y-1 border-t border-white/10 px-5 py-4 text-[11px] text-sidebar-muted">
        <p>
          版本 {APP_VERSION} · SOP {SOP_VERSION}
        </p>
        <p>Cloudflare Pages + Worker</p>
      </div>
    </div>
  );
}

export function AppShell() {
  const { data: me, isLoading } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const title = PAGE_TITLES[pathname] ?? "工作台";

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const handler = () => {
      queryClient.setQueryData(["me"], null);
      queryClient.clear();
      navigate({ to: "/login", replace: true });
    };
    window.addEventListener("edm:unauthorized", handler);
    return () => window.removeEventListener("edm:unauthorized", handler);
  }, [navigate]);

  const canSeeReminders = Boolean(me && hasPermission(me.permissions, "menu:reminders"));
  const canSeeSettings = Boolean(me && hasPermission(me.permissions, "menu:admin_access"));
  const remindersQuery = useQuery({
    queryKey: ["header-reminders"],
    queryFn: () =>
      apiFetch<{
        items: { id: string; severity: string; title: string; dueAt: string | null }[];
        counts: { total: number };
      }>("/api/v1/reminders"),
    enabled: canSeeReminders,
    refetchInterval: 60_000,
  });
  const reminderItems = remindersQuery.data?.items.slice(0, 4) ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-sm space-y-3">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }
  if (!me) return null;

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-sidebar lg:block">
        <SidebarContent pathname={pathname} />
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            className="absolute inset-0 bg-slate-950/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-sidebar shadow-2xl">
            <div className="absolute right-3 top-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-sidebar-foreground hover:bg-white/10 hover:text-white"
                onClick={() => setMobileOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SidebarContent pathname={pathname} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="打开菜单"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                企业电子设备管理
              </p>
              <h2 className="truncate text-base font-semibold">{title}</h2>
            </div>

            <Button
              variant="outline"
              className="hidden h-9 w-56 justify-start gap-2 text-muted-foreground sm:flex"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left text-xs">搜索资产、物料、申请…</span>
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px]">
                ⌘K
              </kbd>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" aria-label="通知">
                  <Bell className="h-5 w-5" />
                  {reminderItems.length > 0 ? (
                    <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-amber-500" />
                  ) : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>通知</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {reminderItems.length === 0 ? (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    暂无新通知
                    <p className="mt-1 text-xs">审批、到期与低库存提醒会出现在这里</p>
                  </div>
                ) : (
                  reminderItems.map((item) => (
                    <DropdownMenuItem key={item.id} asChild>
                      <Link to={"/reminders" as never} className="flex flex-col items-start gap-1">
                        <span className="text-sm">{item.title}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {item.severity === "overdue"
                            ? "已逾期"
                            : item.severity === "due_soon"
                              ? "即将到期"
                              : "提醒"}
                          {item.dueAt ? ` · ${formatDateTime(item.dueAt)}` : ""}
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  ))
                )}
                {canSeeReminders ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={"/reminders" as never}>打开提醒中心</Link>
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="账号菜单"
                  className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-2 text-left transition-colors hover:bg-muted"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{initials(me.user.displayName)}</AvatarFallback>
                  </Avatar>
                  <span className="hidden min-w-0 sm:block">
                    <span className="block max-w-28 truncate text-xs font-medium">
                      {me.user.displayName}
                    </span>
                    <span className="block max-w-28 truncate text-[10px] text-muted-foreground">
                      {me.user.roles.map((role) => role.name).join(" / ") || "无角色"}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <span className="block text-sm font-medium text-foreground">
                    {me.user.displayName}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    上次登录：{formatDateTime(me.user.lastLoginAt)}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="flex flex-wrap gap-1 px-2 pb-2">
                  {me.user.roles.map((role) => (
                    <Badge key={role.code} variant="secondary">
                      {role.name}
                    </Badge>
                  ))}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/change-password">
                    <Settings className="h-4 w-4" />
                    修改密码
                  </Link>
                </DropdownMenuItem>
                {canSeeSettings ? (
                  <DropdownMenuItem asChild>
                    <Link to={"/settings/access" as never}>
                      <Settings className="h-4 w-4" />
                      用户与角色
                    </Link>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-700"
                  onSelect={() => {
                    logout.mutate(undefined, {
                      onSuccess: () => navigate({ to: "/login", replace: true }),
                      onError: (error) => toast.error(errorMessage(error)),
                    });
                  }}
                >
                  <LogOut className="h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
